/* global window */
import AppContext from './context/AppContext';
import { CartContext } from './context/CartContext';
import CartItem from './CartItem';
import getSymbolFromCurrency from 'currency-symbol-map';
import { Link } from 'react-router-dom';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import useCheckout from '../hooks/useCheckout';
import '../styles/CartOverlay.css';
import CartDiscountCodes from './CartDiscountCodes';
import getAvailableQty from '../helpers/getAvailableQty';
import { object } from 'prop-types';

const CART_OVERLAY_ACTION_CLASSNAME = 'cart-overlay--action-info';

const CartOverlay = ({ cartButtonRef }) => {
    const { setStartCheckout } = useCheckout();

    const { appContext } = useContext(AppContext);
    const {
        cartState,
        checkoutData,
        showCartOverlay,
        setShowCartOverlay,
    } = useContext(CartContext);

    const containerAnimation = useMemo(
        () => {
            let overlayClass = '';
            if (showCartOverlay === true) {
                overlayClass = 'slide-in';
            } else if (showCartOverlay === false) {
                overlayClass = 'slide-out';
            }
            return overlayClass;
        }, [showCartOverlay],
    );

    const subtotal = useMemo(() => cartState
        ?.map((item) => parseFloat(item.price * item.quantity))
        .reduce((prev, cur) => +prev + +cur, 0)
        ?? 0, [ cartState ]);

    const cartRef = useRef(null);

    const appConfig = appContext.config.themeConfig;

    useEffect(() => {
        const detectOutsideClick = (e) => {
            if (showCartOverlay !== true) {
                return;
            }
            if (cartRef.current
                && !cartRef.current.contains(e.target) && (!cartButtonRef?.current?.contains(e.target))) {
                setShowCartOverlay(false);
            }
        };
        window.addEventListener('click', detectOutsideClick, true);
        return () => {
            window.removeEventListener('click', detectOutsideClick, true);
        };
    }, [showCartOverlay]);

    const handleCheckoutClick = useCallback(() => {
        setStartCheckout(true);
    }, [setStartCheckout]);

    const handleViewCartClick = useCallback(() => {
        setShowCartOverlay(false);
    }, [setShowCartOverlay]);

    return (
        <>
            <div
                className={`cart-overlay--background-overlay ${showCartOverlay ? 'visible' : ''}`}
            />
            <div
                className={`cart-overlay--container ${containerAnimation}`}
                ref={cartRef}>
                <div
                    className={'cart-overlay--body'}>
                    {cartState?.length > 0
                        ? (cartState.map((lineItem) => (
                            <CartItem
                                key={lineItem.lineItemId}
                                lineItemId={lineItem.lineItemId}
                                inventoryLevelId={lineItem.inventoryLevelId}
                                price={lineItem.price}
                                quantity={lineItem.quantity}
                                sku={lineItem.sku}
                                title={lineItem.title}
                                availableQuantity={getAvailableQty(lineItem)}
                                availableForSale={lineItem.availableForSale}
                                imageUrl={lineItem.imageUrl}
                                handle={lineItem.handle}
                                bypassNegativeQuantity={lineItem.bypassNegativeQuantity}
                            />
                        )))
                        : <div
                            className={'cart-overlay--empty-cart-message'}>
                    No items in cart
                        </div>
                    }
                </div>
                <div
                    className={'cart-overlay--actions'}>
                    <div
                        className={CART_OVERLAY_ACTION_CLASSNAME}>
                        <span>Total Items:</span>
                        <span>
                            {cartState?.reduce((accumulator, item) => accumulator + item.quantity, 0) ?? 0}
                        </span>
                    </div>
                    <div
                        className={CART_OVERLAY_ACTION_CLASSNAME}>
                        <span>Subtotal:</span>
                        <span>
                            {getSymbolFromCurrency(appConfig.storeCurrency)}
                            {subtotal.toFixed(2)}
                        </span>
                    </div>
                    <div
                        className={CART_OVERLAY_ACTION_CLASSNAME}>
                        <span>Discount:</span>
                        <span>
                            {getSymbolFromCurrency(appConfig.storeCurrency)}
                            {(subtotal - (checkoutData?.subtotalPrice ?? 0)).toFixed(2)}
                        </span>
                    </div>
                    {checkoutData?.CheckoutShippingLine && <div
                        className={CART_OVERLAY_ACTION_CLASSNAME}>
                        <span>Shipping:</span>
                        <span>
                            {checkoutData.CheckoutShippingLine.price > 0
                                ? `${getSymbolFromCurrency(appConfig.storeCurrency)}${checkoutData.CheckoutShippingLine.price.toFixed(2)}`
                                : 'FREE'
                            }
                        </span>
                    </div>}
                    <div
                        className={CART_OVERLAY_ACTION_CLASSNAME}>
                        <span>Total Amount:</span>
                        <span>
                            {getSymbolFromCurrency(appConfig.storeCurrency)}
                            {(checkoutData?.totalPrice ?? 0).toFixed(2)}
                        </span>
                    </div>
                    <div
                        className={'w-100'}>
                        <CartDiscountCodes />
                    </div>
                    <div
                        className={'cart-overlay--action-buttons'}>
                        <Link
                            className={'cart-overlay--action-button viewcart'}
                            to={'/shoppingcart.asp'}
                            onClick={handleViewCartClick}>View Cart</Link>
                        <Link
                            aria-label={'Checkout'}
                            className={'cart-overlay--action-button checkout'}
                            onClick={handleCheckoutClick}>
                    Checkout
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

CartOverlay.propTypes = {
    cartButtonRef: object
};

export default CartOverlay;